import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import { astraAPI } from '../services/astraApi';

/**
 * Trading Mode Toggle Component
 *
 * 🔧 PRODUCTION FIX (2025-12-28): Fixed UI transparency issues and API calls
 * - Fixed transparent dropdown panel (was overlapping and unreadable)
 * - Changed from hardcoded localhost to astraAPI client
 * - Added solid background with proper z-index
 */
const TradingModeToggle = () => {
  const [mode, setMode] = useState('paper');
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    fetchMode();
  }, []);

  const fetchMode = async () => {
    try {
      // 🔧 FIX: Use astraAPI client instead of hardcoded URL
      const data = await astraAPI.getTradingMode();
      setMode(data.mode);
    } catch (error) {
      console.error('Failed to fetch trading mode:', error);
    }
  };

  const toggleMode = async () => {
    const newMode = mode === 'paper' ? 'live' : 'paper';

    // Show confirmation for live trading
    if (newMode === 'live') {
      const confirmed = window.confirm(
        '⚠️ SWITCH TO LIVE TRADING?\n\n' +
        'This will use REAL MONEY from your broker accounts:\n' +
        '• Kite (Zerodha) - Indian Stocks\n' +
        '• Binance - Cryptocurrency\n\n' +
        'Are you absolutely sure you want to continue?'
      );
      if (!confirmed) return;
    }

    setLoading(true);
    try {
      // 🔧 FIX: Use astraAPI client instead of hardcoded URL
      const data = await astraAPI.setTradingMode(newMode);
      setMode(newMode);
      alert(
        `✅ ${data.message || 'Trading mode switched successfully'}\n\n` +
        'Please restart the ASTRA system for changes to take effect.'
      );
    } catch (error) {
      console.error('Failed to switch mode:', error);
      alert(`❌ Failed to switch mode: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          flex items-center space-x-2 px-3 py-1.5 rounded-lg cursor-pointer transition-all
          ${mode === 'live'
            ? 'bg-error/10 border border-error/20 hover:bg-error/20'
            : 'bg-success/10 border border-success/20 hover:bg-success/20'
          }
        `}
      >
        {/* Mode Indicator */}
        <div className={`w-2 h-2 rounded-full ${
          mode === 'live' ? 'bg-error animate-pulse' : 'bg-success'
        }`} />

        {/* Mode Label */}
        <span className={`text-sm font-medium ${
          mode === 'live' ? 'text-error' : 'text-success'
        }`}>
          {mode === 'paper' ? '📄 Paper' : '🔴 LIVE'}
        </span>

        {/* Expand Arrow */}
        <Icon
          name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
          size={14}
          className={mode === 'live' ? 'text-error' : 'text-success'}
        />
      </div>

      {/* Expanded Panel - 🔧 PRODUCTION FIX: Solid background, no transparency */}
      {isExpanded && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-2xl z-[100]">
          <div className="p-4 space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between pb-2 border-b border-border">
              <h3 className="font-semibold text-foreground">Trading Mode</h3>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>

            {/* Current Mode Info */}
            <div className={`p-3 rounded-lg ${
              mode === 'live' ? 'bg-error/10' : 'bg-success/10'
            }`}>
              <div className="flex items-center space-x-2 mb-1">
                <div className={`w-2 h-2 rounded-full ${
                  mode === 'live' ? 'bg-error animate-pulse' : 'bg-success'
                }`} />
                <span className={`text-sm font-medium ${
                  mode === 'live' ? 'text-error' : 'text-success'
                }`}>
                  {mode === 'paper' ? 'Paper Trading Active' : 'LIVE Trading Active'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground ml-4">
                {mode === 'paper'
                  ? 'Using simulated funds with live market data'
                  : 'Using REAL MONEY from broker accounts'
                }
              </p>
            </div>

            {/* Details */}
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Data Source:</span>
                <span className="text-foreground font-medium">Live (Kite + KuCoin)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">AI Decisions:</span>
                <span className="text-foreground font-medium">Real (Multi-model)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Calculations:</span>
                <span className="text-foreground font-medium">Real (Fees + Slippage)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Funds:</span>
                <span className={`font-medium ${
                  mode === 'live' ? 'text-error' : 'text-success'
                }`}>
                  {mode === 'paper' ? 'Simulated' : 'Real Broker Balance'}
                </span>
              </div>
            </div>

            {/* Toggle Button */}
            <Button
              onClick={toggleMode}
              disabled={loading}
              variant={mode === 'live' ? 'destructive' : 'default'}
              size="sm"
              className="w-full"
              iconName={mode === 'live' ? 'ShieldOff' : 'Shield'}
              iconPosition="left"
            >
              {loading
                ? 'Switching...'
                : `Switch to ${mode === 'paper' ? 'Live' : 'Paper'} Trading`
              }
            </Button>

            {/* Warning */}
            {mode === 'paper' && (
              <div className="flex items-start space-x-2 p-2 bg-warning/10 border border-warning/20 rounded">
                <Icon name="AlertTriangle" size={14} className="text-warning mt-0.5" />
                <p className="text-xs text-warning">
                  Switching to LIVE will use real broker funds. Ensure you understand the risks.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingModeToggle;
